// src/pages/users/Users.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
  Stack,
  Fab,
  Badge,
  Skeleton,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Email as EmailIcon,
  EuroSymbol as EuroSymbolIcon,
  AttachMoney as AttachMoneyIcon,
  PersonAdd as PersonAddIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Phone as PhoneIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  PhoneIphone as SmartphoneIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, getDoc, doc, setDoc, updateDoc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../../../firebase';

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  success: '#7ED321',
  warning: '#F5A623',
  error: '#E74C3C',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D'
  },
  status: {
    active: '#7ED321',
    inactive: '#BDC3C7',
    paid: '#27AE60',
    unpaid: '#E74C3C'
  }
};

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Orden por fecha de pago de la cuota: null (por email) | 'desc' (últimos primero) | 'asc' (primeros primero)
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [ampaCuote, setAmpaCuote] = useState(30);
  const [yearStartMonth, setYearStartMonth] = useState(9);
  
  // Estados para el diálogo de confirmación de pago
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Estados para el diálogo de cambio de rol
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [processingRoleChange, setProcessingRoleChange] = useState(false);

  // Estados para las bajas (familias que ya no estan en el colegio)
  const [showBajas, setShowBajas] = useState(false);
  const [bajaTarget, setBajaTarget] = useState(null);
  const [bajaChildren, setBajaChildren] = useState([]);
  const [processingBaja, setProcessingBaja] = useState(false);

  // Estado para la notificación
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    getDoc(doc(db, 'settings', 'config')).then(snap => {
      if (snap.exists()) {
        const d = snap.data();
        if (d['ampa-cuote']) setAmpaCuote(Number(d['ampa-cuote']));
        if (d.academicYearStartMonth) setYearStartMonth(d.academicYearStartMonth);
      }
    });
  }, []);

  const getCurrentAcademicYear = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return month >= yearStartMonth ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  }, [yearStartMonth]);

  // Memoizar usuarios filtrados para optimizar rendimiento
  const filteredUsers = useMemo(() => {
    const visibles = showBajas ? users : users.filter(user => user.baja !== true);

    let result = visibles;
    if (searchTerm) {
      const lowercaseSearch = searchTerm.toLowerCase();
      result = visibles.filter(user => {
        const firstName = (user.firstName || '').toLowerCase();
        const lastName = (user.lastName || '').toLowerCase();
        const email = user.email.toLowerCase();
        const phone = user.phone || '';

        return firstName.includes(lowercaseSearch) ||
               lastName.includes(lowercaseSearch) ||
               email.includes(lowercaseSearch) ||
               phone.includes(searchTerm);
      });
    }

    if (!paymentOrder) return result;

    // Fecha de pago en ms; los usuarios sin pago van siempre al final
    const paymentTime = (user) => {
      const raw = user.ampaPayment?.paymentDate || user.ampaPayment?.createdAt;
      if (!raw) return null;
      const t = typeof raw.toDate === 'function' ? raw.toDate().getTime() : new Date(raw).getTime();
      return Number.isNaN(t) ? null : t;
    };
    const dir = paymentOrder === 'asc' ? 1 : -1;
    return [...result].sort((a, b) => {
      const ta = paymentTime(a);
      const tb = paymentTime(b);
      if (ta === null && tb === null) return 0;
      if (ta === null) return 1;
      if (tb === null) return -1;
      return (ta - tb) * dir;
    });
  }, [searchTerm, users, showBajas, paymentOrder]);

  const handleTogglePaymentOrder = useCallback(() => {
    setPaymentOrder(prev => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null));
    setPage(0);
  }, []);

  // Estadísticas calculadas
  const stats = useMemo(() => {
    const currentYear = getCurrentAcademicYear();
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.active).length;
    const paidCurrentYear = users.filter(u => u.ampaPayment?.academicYear === currentYear).length;
    const needsRenewal = users.filter(u => u.ampaPayment && u.ampaPayment.academicYear !== currentYear).length;
    const totalChildren = users.reduce((sum, u) => sum + (u.childrenCount || 0), 0);

    return {
      totalUsers,
      activeUsers,
      paidUsers: paidCurrentYear,
      needsRenewal,
      totalChildren,
      currentYear,
      unpaidUsers: totalUsers - paidCurrentYear,
      paidPercentage: totalUsers > 0 ? Math.round((paidCurrentYear / totalUsers) * 100) : 0
    };
  }, [users, getCurrentAcademicYear]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch users
      const usersQuery = query(collection(db, 'users'), orderBy('email'));
      const [usersSnapshot, paymentsSnapshot] = await Promise.all([
        getDocs(usersQuery),
        getDocs(collection(db, 'ampaPayments'))
      ]);
      
      // Create payments map
      const paymentsMap = new Map();
      paymentsSnapshot.forEach(doc => {
        const payment = doc.data();
        paymentsMap.set(payment.userId, payment);
      });
      
      // Combine users with payment info
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        ampaPayment: paymentsMap.get(doc.id) || null
      }));
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error al cargar los usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const loadUsers = async () => {
      if (isMounted) {
        await fetchUsers();
      }
    };
    
    loadUsers();
    
    return () => {
      isMounted = false;
    };
  }, [fetchUsers]);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  }, []);

  const handleViewUser = useCallback((id) => {
    navigate(`/admin/users/${id}`);
  }, [navigate]);
  
  // --- Bajas de familias --------------------------------------------------
  // No se borra el usuario: se marca baja:true. Borrarlo no eliminaria su
  // cuenta de acceso (eso requiere el Admin SDK) y dejaria sus pedidos e
  // inscripciones sin nombre ni email.

  const handleOpenBajaDialog = useCallback(async (user) => {
    setBajaTarget(user);
    setBajaChildren([]);
    try {
      const snap = await getDocs(query(collection(db, 'children'), where('parentId', '==', user.id)));
      setBajaChildren(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error al cargar los hijos de la familia:', error);
    }
  }, []);

  const handleToggleBaja = useCallback(async (user, baja) => {
    setProcessingBaja(true);
    try {
      await updateDoc(doc(db, 'users', user.id), {
        baja,
        fechaBaja: baja ? serverTimestamp() : null
      });

      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === user.id ? { ...u, baja } : u))
      );

      showSnackbar(
        baja
          ? `${user.firstName || user.email} dado de baja`
          : `${user.firstName || user.email} reactivado`,
        'success'
      );
    } catch (error) {
      console.error('Error al actualizar la baja:', error);
      showSnackbar('No se pudo actualizar la familia', 'error');
    } finally {
      setProcessingBaja(false);
      setBajaTarget(null);
      setBajaChildren([]);
    }
  }, [showSnackbar]);

  const hijosActivos = useMemo(
    () => bajaChildren.filter((c) => c.baja !== true),
    [bajaChildren]
  );

  const handleOpenPaymentDialog = useCallback((user) => {
    setSelectedUser(user);
    setOpenPaymentDialog(true);
  }, []);
  
  const handleClosePaymentDialog = useCallback(() => {
    setOpenPaymentDialog(false);
    setSelectedUser(null);
  }, []);
  
  const handleConfirmPayment = async () => {
    if (!selectedUser) return;
    
    setProcessingPayment(true);
    try {
      const currentYear = getCurrentAcademicYear();
      const paymentData = {
        userId: selectedUser.id,
        amount: ampaCuote,
        paymentMethod: 'efectivo',
        paymentDate: serverTimestamp(),
        academicYear: currentYear,
        status: 'completed',
        createdBy: 'admin',
        createdAt: serverTimestamp(),
        notes: 'Pago en efectivo registrado manualmente'
      };
      
      await setDoc(doc(db, 'ampaPayments', selectedUser.id), paymentData);
      
      // Actualizar estado local
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === selectedUser.id 
            ? { ...user, ampaPayment: paymentData }
            : user
        )
      );
      
      showSnackbar('Pago registrado correctamente', 'success');
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      showSnackbar('Error al registrar el pago', 'error');
    } finally {
      setProcessingPayment(false);
      handleClosePaymentDialog();
    }
  };
  
  const handleOpenRoleDialog = useCallback((user) => {
    setSelectedUserForRole(user);
    setOpenRoleDialog(true);
  }, []);

  const handleCloseRoleDialog = useCallback(() => {
    setOpenRoleDialog(false);
    setSelectedUserForRole(null);
  }, []);

  const handleToggleRole = async () => {
    if (!selectedUserForRole) return;

    setProcessingRoleChange(true);
    try {
      const currentRole = selectedUserForRole.role || 'user';
      const newRole = currentRole === 'admin' ? 'user' : 'admin';

      // Actualizar el rol en Firestore
      const userDocRef = doc(db, 'users', selectedUserForRole.id);
      await updateDoc(userDocRef, {
        role: newRole,
        updatedAt: serverTimestamp()
      });

      // Actualizar estado local
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.id === selectedUserForRole.id
            ? { ...user, role: newRole }
            : user
        )
      );

      const roleLabel = newRole === 'admin' ? 'Administrador' : 'Usuario';
      showSnackbar(`Rol actualizado correctamente a ${roleLabel}`, 'success');
    } catch (error) {
      console.error('Error al cambiar el rol:', error);
      showSnackbar('Error al cambiar el rol del usuario', 'error');
    } finally {
      setProcessingRoleChange(false);
      handleCloseRoleDialog();
    }
  };
  
  // Fecha y forma del ultimo pago o renovacion de la cuota.
  // Los pagos manuales se registran con paymentMethod 'efectivo' desde este panel;
  // los de la app llegan por Stripe con paymentMethod 'card'.
  const getPaymentOriginInfo = useCallback((user) => {
    const payment = user.ampaPayment;
    if (!payment) return null;

    const raw = payment.paymentDate || payment.createdAt;
    let fecha = '';
    if (raw) {
      const dateObj = typeof raw.toDate === 'function' ? raw.toDate() : new Date(raw);
      if (!Number.isNaN(dateObj.getTime())) {
        fecha = dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    }

    const esManual = payment.paymentMethod === 'efectivo' || payment.createdBy === 'admin';

    return {
      fecha,
      curso: payment.academicYear || '',
      metodo: esManual ? 'Efectivo' : 'App',
      esManual
    };
  }, []);

  const getPaymentStatusInfo = useCallback((user) => {
    const currentYear = getCurrentAcademicYear();
    const payment = user.ampaPayment;

    if (!payment) {
      return {
        label: 'No pagado',
        variant: 'unpaid',
        icon: <CancelIcon fontSize="small" />,
        bgColor: COLORS.status.unpaid
      };
    }

    if (payment.academicYear !== currentYear) {
      return {
        label: `Renovar ${currentYear}`,
        variant: 'renewal',
        icon: <AutorenewIcon fontSize="small" />,
        bgColor: COLORS.status.warning,
        prevYear: payment.academicYear
      };
    }

    return {
      label: `Pagado ${currentYear}`,
      variant: 'paid',
      icon: <CheckCircleIcon fontSize="small" />,
      bgColor: COLORS.status.paid
    };
  }, [getCurrentAcademicYear]);
  
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const StatsCard = ({ title, value, subtitle, color, icon }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        border: `1px solid ${color}30`,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 25px ${color}20`
        }
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: `${color}20`,
            color: color 
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
              {value}
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.text.primary, fontWeight: 500 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: COLORS.text.secondary }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  const TableSkeleton = () => (
    <>
      {[...Array(rowsPerPage)].map((_, index) => (
        <TableRow key={index}>
          {[...Array(8)].map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton variant="text" width="100%" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: COLORS.background, minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
          Gestión de Usuarios
        </Typography>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Exportar datos">
            <IconButton 
              sx={{ 
                backgroundColor: COLORS.surface,
                '&:hover': { backgroundColor: COLORS.primary + '20' }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filtros">
            <IconButton 
              sx={{ 
                backgroundColor: COLORS.surface,
                '&:hover': { backgroundColor: COLORS.primary + '20' }
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Usuarios"
            value={stats.totalUsers}
            subtitle="Registrados en el sistema"
            color={COLORS.accent}
            icon={<PersonAddIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Usuarios Activos"
            value={stats.activeUsers}
            subtitle={`${Math.round((stats.activeUsers / stats.totalUsers) * 100) || 0}% del total`}
            color={COLORS.status.active}
            icon={<PersonAddIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={`Pagado ${stats.currentYear}`}
            value={stats.paidUsers}
            subtitle={`${stats.paidPercentage}% del total`}
            color={COLORS.status.paid}
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Pendiente renovación"
            value={stats.needsRenewal}
            subtitle={`${stats.unpaidUsers - stats.needsRenewal} sin pagar nunca`}
            color={COLORS.status.warning}
            icon={<AutorenewIcon />}
          />
        </Grid>
      </Grid>

      {/* Search and Table */}
      <Paper 
        sx={{ 
          backgroundColor: COLORS.surface,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ p: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por nombre, email o teléfono..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: COLORS.background,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.accent,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: COLORS.accent,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: COLORS.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          <FormControlLabel
            control={<Switch checked={showBajas} onChange={(e) => setShowBajas(e.target.checked)} />}
            label="Mostrar familias de baja"
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: COLORS.background }}>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Nombre
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Teléfono
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Rol
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  <Tooltip title={
                    paymentOrder === 'desc' ? 'Últimos en pagar primero (clic: primeros en pagar)'
                    : paymentOrder === 'asc' ? 'Primeros en pagar primero (clic: quitar orden)'
                    : 'Ordenar por fecha de pago'
                  }>
                    <TableSortLabel
                      active={paymentOrder !== null}
                      direction={paymentOrder || 'desc'}
                      onClick={handleTogglePaymentOrder}
                    >
                      Pago cuota
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Hijos
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Cuota AMPA
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios registrados'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => {
                    const paymentStatus = getPaymentStatusInfo(user);
                    
                    return (
                      <TableRow 
                        key={user.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: COLORS.primary + '10',
                          },
                        }}
                      >
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {user.firstName || ''} {user.lastName || ''}
                            </Typography>
                            {(user.firstName || user.lastName) === '' && (
                              <Typography variant="body2" color="text.secondary">
                                Sin nombre
                              </Typography>
                            )}
                            {user.baja === true && (
                              <Chip
                                icon={<ArchiveIcon fontSize="small" />}
                                label="Baja"
                                size="small"
                                sx={{ mt: 0.5, backgroundColor: COLORS.status.inactive + '30', color: COLORS.status.inactive, fontWeight: 'bold' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.phone || 'No especificado'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={user.role === 'admin' ? <AdminIcon fontSize="small" /> : <UserIcon fontSize="small" />}
                            label={user.role === 'admin' ? 'Admin' : 'Usuario'}
                            sx={{
                              backgroundColor: user.role === 'admin' ? '#FF6B6B20' : COLORS.accent + '20',
                              color: user.role === 'admin' ? '#FF6B6B' : COLORS.accent,
                              fontWeight: 'bold',
                              border: `1px solid ${user.role === 'admin' ? '#FF6B6B30' : COLORS.accent + '30'}`
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const origen = getPaymentOriginInfo(user);
                            if (!origen) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  Sin pago registrado
                                </Typography>
                              );
                            }
                            return (
                              <Box>
                                <Chip
                                  icon={origen.esManual
                                    ? <EuroSymbolIcon fontSize="small" />
                                    : <SmartphoneIcon fontSize="small" />}
                                  label={origen.esManual ? 'Efectivo (manual)' : 'App'}
                                  size="small"
                                  sx={{
                                    backgroundColor: origen.esManual ? '#F5A62320' : COLORS.accent + '20',
                                    color: origen.esManual ? '#B57A12' : COLORS.accent,
                                    fontWeight: 'bold'
                                  }}
                                />
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {origen.fecha || 'Sin fecha'}
                                  {origen.curso ? ` · curso ${origen.curso}` : ''}
                                </Typography>
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            badgeContent={user.childrenCount || 0} 
                            color="primary"
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: COLORS.accent,
                                color: 'white'
                              }
                            }}
                          >
                            <PersonAddIcon />
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={
                            paymentStatus.variant === 'renewal'
                              ? `Pagó en ${paymentStatus.prevYear} — necesita renovar para ${getCurrentAcademicYear()}`
                              : paymentStatus.variant === 'paid'
                              ? `Cuota pagada para el año ${getCurrentAcademicYear()}`
                              : 'Sin pago registrado'
                          }>
                            <Chip
                              icon={paymentStatus.icon}
                              label={paymentStatus.label}
                              sx={{
                                backgroundColor: paymentStatus.bgColor + '20',
                                color: paymentStatus.bgColor,
                                fontWeight: 'bold',
                                border: `1px solid ${paymentStatus.bgColor}40`
                              }}
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title={user.role === 'admin' ? 'Cambiar a Usuario' : 'Cambiar a Admin'}>
                              <IconButton
                                onClick={() => handleOpenRoleDialog(user)}
                                sx={{
                                  color: user.role === 'admin' ? '#FF6B6B' : COLORS.accent,
                                  '&:hover': { backgroundColor: user.role === 'admin' ? '#FF6B6B20' : COLORS.accent + '20' }
                                }}
                              >
                                {user.role === 'admin' ? <AdminIcon fontSize="small" /> : <UserIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            {user.baja === true ? (
                              <Tooltip title="Reactivar familia">
                                <IconButton
                                  onClick={() => handleToggleBaja(user, false)}
                                  disabled={processingBaja}
                                  sx={{ color: COLORS.status.active }}
                                >
                                  <UnarchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Dar de baja (ya no está en el colegio)">
                                <IconButton
                                  onClick={() => handleOpenBajaDialog(user)}
                                  disabled={processingBaja}
                                  sx={{ color: '#F5A623' }}
                                >
                                  <ArchiveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Ver detalles">
                              <IconButton
                                onClick={() => handleViewUser(user.id)}
                                sx={{
                                  color: COLORS.accent,
                                  '&:hover': { backgroundColor: COLORS.accent + '20' }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Enviar email">
                              <IconButton
                                href={`mailto:${user.email}`}
                                sx={{
                                  color: COLORS.text.secondary,
                                  '&:hover': { backgroundColor: COLORS.primary + '20' }
                                }}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {user.phone && (
                              <Tooltip title="Llamar">
                                <IconButton
                                  href={`tel:${user.phone}`}
                                  sx={{
                                    color: COLORS.text.secondary,
                                    '&:hover': { backgroundColor: COLORS.primary + '20' }
                                  }}
                                >
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {paymentStatus.variant !== 'paid' && (
                              <Tooltip title={
                                paymentStatus.variant === 'renewal'
                                  ? `Renovar cuota ${getCurrentAcademicYear()} (efectivo)`
                                  : 'Registrar pago en efectivo'
                              }>
                                <IconButton
                                  onClick={() => handleOpenPaymentDialog(user)}
                                  sx={{
                                    color: paymentStatus.variant === 'renewal' ? COLORS.status.warning : COLORS.status.paid,
                                    '&:hover': { backgroundColor: paymentStatus.bgColor + '20' }
                                  }}
                                >
                                  {paymentStatus.variant === 'renewal'
                                    ? <AutorenewIcon fontSize="small" />
                                    : <EuroSymbolIcon fontSize="small" />
                                  }
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          sx={{
            borderTop: `1px solid ${COLORS.primary}30`,
            backgroundColor: COLORS.background
          }}
        />
      </Paper>
      
      {/* Payment Confirmation Dialog */}
      <Dialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
          {selectedUser && getPaymentStatusInfo(selectedUser).variant === 'renewal'
            ? `Renovar cuota AMPA ${getCurrentAcademicYear()}`
            : 'Registrar pago de cuota AMPA'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: COLORS.text.secondary }}>
            ¿Registrar el pago en efectivo de la cuota AMPA para{' '}
            <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>?
          </DialogContentText>
          {selectedUser && getPaymentStatusInfo(selectedUser).variant === 'renewal' && (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
              Tiene registrado un pago del año <strong>{selectedUser.ampaPayment?.academicYear}</strong>. Se registrará la renovación para <strong>{getCurrentAcademicYear()}</strong>.
            </Alert>
          )}
          <Box sx={{ mt: 2, p: 2, backgroundColor: COLORS.background, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Importe:</strong> {ampaCuote.toFixed(2)} €<br />
              <strong>Método:</strong> Efectivo<br />
              <strong>Año académico:</strong> {getCurrentAcademicYear()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClosePaymentDialog}
            disabled={processingPayment}
            sx={{ color: COLORS.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPayment}
            variant="contained"
            disabled={processingPayment}
            sx={{
              backgroundColor: COLORS.accent,
              '&:hover': { backgroundColor: COLORS.secondary },
              '&:disabled': { backgroundColor: COLORS.text.secondary }
            }}
          >
            {processingPayment ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Confirmar pago'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Confirmation Dialog */}
      <Dialog
        open={openRoleDialog}
        onClose={handleCloseRoleDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ color: COLORS.text.primary, fontWeight: 'bold' }}>
          Cambiar Rol de Usuario
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: COLORS.text.secondary, mb: 2 }}>
            ¿Está seguro que desea cambiar el rol de{' '}
            <strong>{selectedUserForRole?.firstName} {selectedUserForRole?.lastName}</strong>?
          </DialogContentText>
          <Box sx={{ p: 2, backgroundColor: COLORS.background, borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
              <Chip
                icon={selectedUserForRole?.role === 'admin' ? <AdminIcon /> : <UserIcon />}
                label={selectedUserForRole?.role === 'admin' ? 'Administrador' : 'Usuario'}
                sx={{
                  backgroundColor: selectedUserForRole?.role === 'admin' ? '#FF6B6B20' : COLORS.accent + '20',
                  color: selectedUserForRole?.role === 'admin' ? '#FF6B6B' : COLORS.accent,
                  fontWeight: 'bold'
                }}
              />
              <Typography variant="h5" sx={{ color: COLORS.text.primary }}>→</Typography>
              <Chip
                icon={selectedUserForRole?.role === 'admin' ? <UserIcon /> : <AdminIcon />}
                label={selectedUserForRole?.role === 'admin' ? 'Usuario' : 'Administrador'}
                sx={{
                  backgroundColor: selectedUserForRole?.role === 'admin' ? COLORS.accent + '20' : '#FF6B6B20',
                  color: selectedUserForRole?.role === 'admin' ? COLORS.accent : '#FF6B6B',
                  fontWeight: 'bold'
                }}
              />
            </Stack>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            {selectedUserForRole?.role === 'admin' ? (
              <Typography variant="body2">
                Al cambiar a Usuario, perderá todos los permisos de administración.
              </Typography>
            ) : (
              <Typography variant="body2">
                Al cambiar a Administrador, tendrá acceso completo al panel de administración.
              </Typography>
            )}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseRoleDialog}
            disabled={processingRoleChange}
            sx={{ color: COLORS.text.secondary }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleToggleRole}
            variant="contained"
            disabled={processingRoleChange}
            sx={{
              backgroundColor: selectedUserForRole?.role === 'admin' ? COLORS.accent : '#FF6B6B',
              '&:hover': { backgroundColor: selectedUserForRole?.role === 'admin' ? COLORS.secondary : '#E55555' },
              '&:disabled': { backgroundColor: COLORS.text.secondary }
            }}
          >
            {processingRoleChange ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              'Confirmar cambio'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: COLORS.accent,
          '&:hover': { backgroundColor: COLORS.secondary }
        }}
        onClick={() => navigate('/admin/users/create')}
      >
        <PersonAddIcon />
      </Fab>
      
      {/* Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? COLORS.success : COLORS.error
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* Confirmar baja de una familia */}
      <Dialog open={Boolean(bajaTarget)} onClose={() => setBajaTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Dar de baja a la familia</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Dar de baja a <strong>{bajaTarget?.firstName} {bajaTarget?.lastName}</strong> ({bajaTarget?.email})?
          </DialogContentText>

          {hijosActivos.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Esta familia todavía tiene {hijosActivos.length} hijo(s) en el colegio:{' '}
              {hijosActivos.map((c) => `${c.nombre || ''} ${c.apellidos || ''}`.trim()).join(', ')}.
              Lo normal es dar de baja primero a los alumnos desde la pantalla de Hijos.
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            No se borra nada: la familia deja de aparecer en los listados, pero sus pedidos,
            inscripciones y la contabilidad se conservan. Puedes reactivarla cuando quieras.
          </Alert>

          <Alert severity="warning" sx={{ mt: 2 }}>
            Ojo: esto no elimina su cuenta de acceso. Si vuelve a entrar en la app, la app le
            pedirá completar el perfil. Para borrar la cuenta de verdad hace falta una Cloud
            Function con el Admin SDK.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBajaTarget(null)} disabled={processingBaja}>Cancelar</Button>
          <Button
            onClick={() => handleToggleBaja(bajaTarget, true)}
            variant="contained"
            color="warning"
            disabled={processingBaja}
            startIcon={processingBaja ? <CircularProgress size={16} /> : <ArchiveIcon />}
          >
            Dar de baja
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Users;